import { useMemo, useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GenericTable, NumberedView } from 'components/tables/generic-table'
import { LessonActionDropdown } from 'components/action-dropdown'
import { CopyToClassOverlay } from 'components/lesson-builder/copy-to-class-overlay'
import { ConfirmationOverlay } from 'components/confirmation-overlay'
import styles from './lessons-table.module.css'
import moreIcon from 'img/more-vert.svg'
import moreIconWhite from 'img/more-vert-white.svg'
import viewGradesIcon from 'img/view-grades.svg'
import { useRenameLessonMutation, useDeleteLessonMutation } from 'util/rtk-api'
import { LessonSearchDetails } from '@all-for-one/shared-types'
// Accept lessons as a prop per requirement

const formatDate = (ts?: number) => {
	if (!ts) return '-'
	const d = new Date(ts)
	const [ , month, day, year ] = d.toDateString().split(' ')
	return `${month} ${day}, ${year}`
}

interface ActionCellProps {
	lessonId: string
	lessonName: string
	isSelected: boolean
	onStartRename: (lessonId: string, currentName: string) => void
	onStartDuplicate: (lessonId: string, lessonName: string, assignedClassId?: string, grades?: string[]) => void
	onDeleteLesson: (lessonId: string, lessonName: string) => void
	isDeletingId: string | null
	hasGradedResults?: boolean
	assignedClassId?: string
	grades?: string[]
}

const ActionCell = ({ lessonId, lessonName, isSelected, onStartRename, onStartDuplicate, onDeleteLesson, isDeletingId, hasGradedResults, assignedClassId, grades }: ActionCellProps) => {
	const [open, setOpen] = useState(false)
	const [opensUp, setOpensUp] = useState(false)
	const dropdownRef = useRef<HTMLDivElement | null>(null)
	const buttonRef = useRef<HTMLImageElement | null>(null)
	const toggle = () => {
		if (!open && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect()
			setOpensUp(window.innerHeight - rect.bottom < 120)
		}
		setOpen(v => !v)
	}

	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (open && dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [open])

	const onDuplicateLesson = () => {
		setOpen(false)
		onStartDuplicate(lessonId, lessonName, assignedClassId, grades)
	}

	const onRenameLesson = () => {
		setOpen(false)
		onStartRename(lessonId, lessonName)
	}

	const handleDelete = () => {
		setOpen(false)
		onDeleteLesson(lessonId, lessonName)
	}

	return (
		<td style={open ? { zIndex: 2 } : undefined}>
			<div className={styles.actionCell} ref={dropdownRef}>
				<img ref={buttonRef} src={isSelected ? moreIconWhite : moreIcon} alt="More" onClick={toggle} />
				<div>
					{open && (
						<LessonActionDropdown
							onDuplicateLesson={onDuplicateLesson}
							onRenameLesson={onRenameLesson}
							onDeleteLesson={handleDelete}
							isDeleting={isDeletingId === lessonId}
							hasGradedResults={hasGradedResults}
							opensUp={opensUp}
						/>
					)}
				</div>
			</div>
		</td>
	)
}

export const LessonOverviewTable = ({ lessons }: { lessons: LessonSearchDetails[] }) => {
	const navigate = useNavigate()
	const [renameLesson] = useRenameLessonMutation()
	const [deleteLesson] = useDeleteLessonMutation()
	const [renamingLessonId, setRenamingLessonId] = useState<string | null>(null)
	const [renameValue, setRenameValue] = useState('')
	const renameInputRef = useRef<HTMLInputElement | null>(null)
	const [deletingLessonId, setDeletingLessonId] = useState<string | null>(null)
	const [deleteConfirm, setDeleteConfirm] = useState<{ lessonId: string; lessonName: string } | null>(null)

	// Duplicate overlay state
	const [duplicateOverlay, setDuplicateOverlay] = useState<{
		lessonId: string
		lessonName: string
		currentClassId: string
		currentGrades: string[]
	} | null>(null)

	const handleStartRename = (lessonId: string, currentName: string) => {
		setRenamingLessonId(lessonId)
		setRenameValue(currentName)
	}

	const handleStartDuplicate = (lessonId: string, lessonName: string, assignedClassId?: string, grades?: string[]) => {
		setDuplicateOverlay({
			lessonId,
			lessonName,
			currentClassId: assignedClassId || '',
			currentGrades: grades || []
		})
	}

	const handleDeleteLesson = (lessonId: string, lessonName: string) => {
		setDeleteConfirm({ lessonId, lessonName })
	}

	const handleConfirmDelete = async () => {
		if (!deleteConfirm) return
		const { lessonId } = deleteConfirm
		setDeletingLessonId(lessonId)
		try {
			await deleteLesson(lessonId).unwrap()
			setDeleteConfirm(null)
		} catch (error) {
			console.error('Error deleting lesson', error)
		} finally {
			setDeletingLessonId(null)
		}
	}

	const handleCancelRename = () => {
		setRenamingLessonId(null)
		setRenameValue('')
	}

	const handleConfirmRename = async () => {
		if (renamingLessonId && renameValue.trim()) {
			try {
				await renameLesson({ lessonId: renamingLessonId, name: renameValue.trim() })
			} catch (error) {
				console.error('Error renaming lesson', error)
			}
		}
		handleCancelRename()
	}

	const handleRenameKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleConfirmRename()
		} else if (e.key === 'Escape') {
			handleCancelRename()
		}
	}

	useEffect(() => {
		if (renamingLessonId && renameInputRef.current) {
			renameInputRef.current.focus()
			renameInputRef.current.select()
		}
	}, [renamingLessonId])

    const rows = useMemo(() => lessons.map(l => ({
        ...l,
        classroom: l.assignedClassName ?? '-'
    })), [lessons])

	const columns = [
		{
			key: 'name',
			header: 'Name',
			render: (name: string, index: number, row: any) => {
				if (renamingLessonId === row.lessonId) {
					return (
						<div className={styles.renameCell}>
							<input
								ref={renameInputRef}
								type="text"
								value={renameValue}
								onChange={(e) => setRenameValue(e.target.value)}
								onKeyDown={handleRenameKeyDown}
								onBlur={handleConfirmRename}
								className={styles.renameInput}
							/>
						</div>
					)
				}
				return (
					<div className={styles.nameCell}>
						<Link className={styles.lessonLink} to={`/lesson-builder?lessonId=${row.lessonId}`}>{name}</Link>
						{row.hasGradedResults && row.assignedClassId && (
							<Link
								className={styles.gradesLink}
								to={`/classrooms/${row.assignedClassId}/lessons/${row.lessonId}`}
							>
								<img src={viewGradesIcon} alt="" className={styles.gradesIcon} />
								Grades
							</Link>
						)}
					</div>
				)
			}
		},
		{ key: 'classroom', header: 'Classroom', className: styles.classroomCol },
		{ key: 'lastViewed', header: 'Last viewed', className: styles.dateCol, render: (v: number) => formatDate(v) },
		{ key: 'lastModified', header: 'Last modified', className: styles.dateCol, render: (v: number) => formatDate(v) },
		{ key: 'status', header: 'Status', className: styles.statusCol }
	]

	return (
		<>
			<div className={styles.tableContainer}>
				<GenericTable
					columns={columns}
					data={rows}
					rowKey="lessonId"
					style={{}}
					renderHeader={true}
					renderMoreActions={true}
					createMoreActionCell={(lessonId: string, isSelected: boolean) => {
						const lesson = rows.find(r => r.lessonId === lessonId)
						return (
							<ActionCell
								lessonId={lessonId}
								lessonName={lesson?.name || ''}
								isSelected={isSelected}
								onStartRename={handleStartRename}
								onStartDuplicate={handleStartDuplicate}
								onDeleteLesson={handleDeleteLesson}
								isDeletingId={deletingLessonId}
								hasGradedResults={lesson?.hasGradedResults}
								assignedClassId={lesson?.assignedClassId}
								grades={lesson?.grades}
							/>
						)
					}}
					doubleClickRowAction={(lessonId: string) => navigate(`/lesson-builder?lessonId=${lessonId}`)}
				/>
			</div>
			{duplicateOverlay && (
				<CopyToClassOverlay
					lessonId={duplicateOverlay.lessonId}
					lessonName={duplicateOverlay.lessonName}
					currentClassId={duplicateOverlay.currentClassId}
					currentGrades={duplicateOverlay.currentGrades}
					onDismiss={() => setDuplicateOverlay(null)}
				/>
			)}
			{deleteConfirm && (
				<ConfirmationOverlay
					title="Delete Lesson"
					message={`Are you sure you want to delete "${deleteConfirm.lessonName}"? This cannot be undone.`}
					confirmLabel="Delete"
					onConfirm={handleConfirmDelete}
					onDismiss={() => setDeleteConfirm(null)}
					isLoading={deletingLessonId === deleteConfirm.lessonId}
				/>
			)}
		</>
	)
}


